import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SegmentedControl, type TabItem } from '../../../components/ui/Tabs';
import { InfoTip } from '../../../components/ui/InfoTip';
import { InsightsTab } from './InsightsTab';
import { CsmAosDashboard } from '../CsmAosDashboard';

type Platform = 'classic' | 'aos';
type Days = '7' | '30' | '90';

const PLATFORMS: readonly TabItem<Platform>[] = [
  { id: 'classic', label: 'Classic platform' },
  { id: 'aos', label: 'aOS platform' },
];

const PERIODS: readonly TabItem<Days>[] = [
  { id: '7', label: '7 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
];

/**
 * CSM › Usage: one tab for both platform generations. The platform choice lives
 * in the URL (?platform=aos) so the old Token Usage aOS link lands here.
 */
export function UsageTab() {
  const [params, setParams] = useSearchParams();
  const platform: Platform = params.get('platform') === 'aos' ? 'aos' : 'classic';
  const [days, setDays] = useState<Days>('30');

  const setPlatform = (next: Platform) => {
    const p = new URLSearchParams(params);
    if (next === 'aos') p.set('platform', 'aos');
    else p.delete('platform');
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <SegmentedControl options={PLATFORMS} value={platform} onChange={setPlatform} ariaLabel="Platform" />
          <InfoTip>
            <span className="font-medium">Classic</span> is the legacy platform: Auth0 login events and its token table.{' '}
            <span className="font-medium">aOS</span> is the new platform (Nova), read from its AI usage ledger. The two are
            counted separately, so their totals do not add up to one figure.
          </InfoTip>
        </div>
        <SegmentedControl options={PERIODS} value={days} onChange={setDays} ariaLabel="Period" />
      </div>

      {platform === 'classic' ? <InsightsTab days={Number(days)} /> : <CsmAosDashboard days={Number(days)} />}
    </div>
  );
}
